// Node packages
import React from 'react';
import { Route, Redirect, Switch, BrowserRouter } from 'react-router-dom';
import Provider from '@react/react-spectrum/Provider';

// Custom components
import AccessDeniedPage from './components/AccessDeniedPage/AccessDeniedPage';
// import DistributorSelectionPage from './ManageDistributors/components/DistributorSelectionPage/DistributorSelectionPage';
import ManageDistributorsPage from './ManageDistributors/ManageDistributorsPage';
import NavigationBar from './components/NavigationBar/NavigationBar';
import InternalNavigationBar from './components/NavigationBar/InternalNavigationBar';
import RecordsPage from './RecordsPage/RecordsPage';
import TransfersPage from './TransfersPage/TransfersPage';
import ToolsPage from './ToolsPage/ToolsPage';
import InternalPage from './InternalPage/InternalPage';
import ResourcesPage from './ResourcesPage/ResourcesPage';

// Custom styling and utils
import './App.css';
import { IMSAccountService } from './service/ims/IMSAccountService';
import { getDistributors } from './providers/DistributorsAPI';

import { checkIfAdobeUser, setDistributorList, isSelectedDistributorProgramRetail } from './service/ApiService';
import {fetchFeatureFlags} from "./service/FloodgateService";
import {isFlagEnabled} from "./config/FeatureFlags";
import RetailRecordsPage from './ToolsPage/RetailRecordsPage';
import { checkPPBHealth } from './providers/HealthAPI';
import MaintenanceBanner from './components/MaintenanceBanner/MaintenanceBanner';

/**
 * Contains entire application.
 * Manages login state for entire application.
 */

function App() {
  const [isLoading, setIsLoading] = React.useState(true);
  const [validProfile, setValidProfile] = React.useState(false);
  const [internalProfile, setInternalProfile] = React.useState(false);
  const [isPPBDown, setIsPPBDown] = React.useState(false);
  const [headerEl, setHeaderEl] = React.useState<HTMLDivElement | null>(null);
  const [headerHeight, setHeaderHeight] = React.useState(120);

  React.useLayoutEffect(() => {
    if (!headerEl || typeof ResizeObserver === 'undefined') return;
    const updateHeight = () => setHeaderHeight(headerEl.getBoundingClientRect().height);
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(headerEl);
    return () => observer.disconnect();
  }, [headerEl]);

  React.useEffect(() => {
    ensureUserProfile().finally(() => setIsLoading(false));

    async function ensureUserProfile() {
      try {
        await IMSAccountService.initialize();

        const ppbHealthy = await checkPPBHealth();
        if (!ppbHealthy) {
          setIsPPBDown(true);
          return;
        }

        const distributorsList = await getDistributors();

        if (distributorsList.length) {
          setDistributorList(distributorsList);

          try {
            const clientIds = ['partnerportal', 'enterprise-services-common'];
            const response = await fetchFeatureFlags(clientIds[0]);
            if (response !== undefined) {
              const result = await fetchFeatureFlags(clientIds[1]);
              if(result !== undefined){
                response.releases[0].features.push(...result.releases[0].features);
              }
              isFlagEnabled(response);
            }
          } catch (error) {
            console.error('Failed to load feature flags, proceeding without flags:', error);
          }

          setValidProfile(true);
        }

        const isAdobeUser = checkIfAdobeUser()
        setInternalProfile(isAdobeUser);
      } catch (error) {
        console.error('failed to initialize', error)
      }
    }
  }, []);

  const ValidatedApp = (
    <div className='App'>
      <div className='App-header-fixed' ref={setHeaderEl}>
        <NavigationBar />
      </div>
      <div className='App-content' style={{ paddingTop: headerHeight }}>
        <MaintenanceBanner />
        <BrowserRouter>
          <Switch>
            <Route exact path='/internal' component={InternalPage} />
            <Route exact path='/tools' component={ToolsPage} />
            <Route exact path='/resources' component={ResourcesPage} />
            <Route exact path='/transfers' component={TransfersPage} />
            <Route exact path='/records' component={RecordsPage} />
            <Route exact path='/distributors' component={ManageDistributorsPage} />
            <Redirect to='/records' />
          </Switch>
        </BrowserRouter>
      </div>
      <div className='App-footer' />
    </div>
  );

  const RetailValidatedApp = (
      <div className='App'>
        <div className='App-header-fixed' ref={setHeaderEl}>
          <NavigationBar />
        </div>
        <div className='App-content' style={{ paddingTop: headerHeight }}>
          <MaintenanceBanner />
          <BrowserRouter>
            <Switch>
              <Route exact path='/internal' component={InternalPage} />
              <Route exact path='/retail/records' component={RetailRecordsPage} />
              <Route exact path='/distributors' component={ManageDistributorsPage} />
              <Redirect to='/retail/records' />
            </Switch>
          </BrowserRouter>
        </div>
        <div className='App-footer' />
      </div>
    );

  const InternalValidatedApp = (
    <div className='App'>
      <div className='App-header-fixed' ref={setHeaderEl}>
        <InternalNavigationBar />
      </div>
      <div className='App-content' style={{ paddingTop: headerHeight }}>
        <MaintenanceBanner />
        <BrowserRouter>
          <Switch>
            <Route exact path='/internal' component={InternalPage} />
            <Redirect to='/internal' />
          </Switch>
        </BrowserRouter>
      </div>
      <div className='App-footer' />
    </div>
  );

  let content: React.ReactElement;
  if (isLoading) {
    content = <AccessDeniedPage isLoading={true} />;
  } else if (isPPBDown) {
    content = <AccessDeniedPage isLoading={false} isServiceDown={true} />;
  } else if (validProfile) {
    content = isSelectedDistributorProgramRetail() ? RetailValidatedApp : ValidatedApp;
  } else if (internalProfile) {
    content = InternalValidatedApp;
  } else {
    content = <AccessDeniedPage isLoading={false} />;
  }

  return (
    <Provider theme='light'>
      {content}
    </Provider>
  );
}

export default App;
